import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useSupplierService } from 'services/suppliers';
import { Supplier } from 'types/supplier';
import Button from 'components/Button';
import styles from './Suppliers.module.scss';

interface DeleteSupplierModalProps {
  show: boolean;
  onHide: () => void;
  supplier: Supplier | null;
  onSuccess: (supplier: Supplier) => void;
  onError: (message: string) => void;
}

function DeleteSupplierModal({
  show,
  onHide,
  supplier,
  onSuccess,
  onError,
}: DeleteSupplierModalProps) {
  const supplierService = useSupplierService();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) {
      setReason('');
    }
  }, [show]);

  if (!supplier) {
    return null;
  }

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const voidedSupplier = await supplierService.voidSupplier(
        supplier.id,
        reason.trim() || undefined,
      );
      onSuccess(voidedSupplier);
      onHide();
    } catch (error) {
      onError(
        error instanceof Error ? error.message : 'Failed to void supplier',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.modal}
      contentClassName={styles.modalContent}
      dialogClassName={styles.modalDialog}
      backdrop="static"
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Void Supplier</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.voidDetails}>
          <div className={styles.detailRow}>
            <strong>Name:</strong>
            <span>{supplier.name}</span>
          </div>
          {supplier.email && (
            <div className={styles.detailRow}>
              <strong>Email:</strong>
              <span>{supplier.email}</span>
            </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="voidReason" className={styles.label}>
            Reason for Voiding (Optional)
          </label>
          <textarea
            id="voidReason"
            placeholder="Enter the reason for voiding this supplier..."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className={styles.textarea}
            rows={4}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button
          type="button"
          variant="outline"
          onClick={onHide}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => {
            void handleConfirm();
          }}
          disabled={isSubmitting}
          className={styles.deleteButton}
        >
          {isSubmitting ? 'Voiding...' : 'Void Supplier'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DeleteSupplierModal;
